import React, { useMemo, useState, useEffect, useRef } from 'react';
import ListBodyLayout from '@/components/layout/ListBodyLayout';
import { Form, Input, InputNumber, Select, Row, Col, Dropdown, Tour } from 'antd';
import CreateProjectModal from '@/components/module/projects/CreateProjectModal';
import {
    PlusOutlined,
    SearchOutlined,
    AimOutlined,
    CheckCircleOutlined,
    UserOutlined,
    WarningOutlined,
    MoreOutlined,
    StarOutlined,
    StarFilled,
    EyeOutlined,
    SwapOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import Table from '@/components/ui/table/Table';
import { SliderTab } from '@/components/ui/SliderTab';
import { Button } from '@/components/ui/Button';
import { ToolbarFilterButton } from '@/components/ui/ToolbarFilterButton';
import { RefreshCwIcon } from '@/components/ui/RefreshCwIcon';
import { useCanCheck } from '@/ability/helpers';
import { useIsClientProfile } from '@hooks/useIsClientProfile';
import { useProjectsPage } from '@hooks/projects/useProjectsPage';
import {
    RolesSliderTabWrap,
    SliderTabStripScroll,
    TenantToolbarSearchWrap,
    TenantToolbarSearchInput,
    TenantToolbarIconBtn,
    TenantToolbarRefreshSpin,
    CreateRoleModalBody,
    CreateRoleModalFooter,
} from './style';

const DragAndDropVisual = () => (
    <div style={{ marginTop: '12px', padding: '12px', background: '#f5f5f5', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <div style={{ padding: '8px 12px', background: '#ffffff', borderRadius: '4px', border: '1px solid #e8e8e8', fontSize: '12px', fontWeight: 500, color: '#8c8c8c' }}>
            📁 Project Alpha
        </div>
        <div style={{ 
            padding: '8px 12px', 
            borderRadius: '4px', 
            border: '1px dashed #1890ff', 
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            color: '#1890ff',
            fontWeight: 500,
            background: 'rgba(24, 144, 255, 0.05)'
        }}>
            Drop target area...
        </div>
        <div style={{ 
            padding: '8px 12px', 
            background: '#e6f7ff', 
            borderRadius: '6px', 
            border: '1px solid #91d5ff', 
            fontSize: '12px', 
            fontWeight: 600, 
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            transform: 'rotate(-1.5deg) translateY(-24px)',
            position: 'relative',
            zIndex: 10,
            cursor: 'grabbing',
        }}>
            📁 Project Beta (Dragging...)
        </div>
        <div style={{ padding: '8px 12px', background: '#ffffff', borderRadius: '4px', border: '1px solid #e8e8e8', fontSize: '12px', fontWeight: 500, color: '#8c8c8c', marginTop: '-16px' }}>
            📁 Project Gamma
        </div>
    </div>
);

const MouseVisual = () => (
    <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px' }}>
            <span style={{ fontSize: '16px' }}>🖱️</span>
            <div>
                <strong>1. Hold down click</strong>
                <div style={{ color: '#8c8c8c' }}>Press & hold the left mouse button for 200ms on a row.</div>
            </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px' }}>
            <span style={{ fontSize: '16px' }}>↕️</span>
            <div>
                <strong>2. Drag to move</strong>
                <div style={{ color: '#8c8c8c' }}>Move the cursor up or down to rearrange the list.</div>
            </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px' }}>
            <span style={{ fontSize: '16px' }}>✔️</span>
            <div>
                <strong>3. Release to Drop</strong>
                <div style={{ color: '#8c8c8c' }}>Release the click to automatically save the position.</div>
            </div>
        </div>
    </div>
);

const Projects = () => {
    const can = useCanCheck();
    const isClient = useIsClientProfile();
    const canCreateProject = can('projects.projects.create') && !isClient;

    const {
        searchText,
        setSearchText,
        handleSearchChange,
        handleSearchKeyDown,
        statusTab,
        setStatusTab,
        STATUS_TAB,
        openProjectDetail,
        createModalOpen,
        setCreateModalOpen,
        createInitialValues,
        total,
        activeCount,
        withClientCount,
        highPriorityCount,
        tableData,
        columns,
        closeCreateModal,
        PRIORITY_OPTIONS,
        PROJECT_TYPE_OPTIONS,
        CLIENT_TYPE_OPTIONS,
        categoryOptions,
        templateOptions,
        clientOptions,
        refetchProjects,
        projectsListRefetching,
        filterModalOpen,
        setFilterModalOpen,
        filterPriorityDraft,
        setFilterPriorityDraft,
        filterProjectTypeDraft,
        setFilterProjectTypeDraft,
        filterTemplateIdDraft,
        setFilterTemplateIdDraft,
        filterCategoryIdDraft,
        setFilterCategoryIdDraft,
        filterClientTypeDraft,
        setFilterClientTypeDraft,
        filterClientIdDraft,
        setFilterClientIdDraft,
        hasActiveFilters,
        clearFilterDraft,
        applyFiltersFromModal,
        clearFilters,
        handleReorderStarredProjects,
        handleReorderProjects,
        handleToggleStar,
    } = useProjectsPage();

    const tourBtnRef = useRef(null);
    const [tourOpen, setTourOpen] = useState(false);

    const tourSteps = useMemo(() => [
        {
            title: 'Manual Project Sorting',
            description: 'You can manually reorder your projects inside the "All" tab at any time to customize your priority layout.',
            target: () => tourBtnRef.current,
        },
        {
            title: 'Hold to Drag',
            description: (
                <div>
                    <div>Click and hold any project row for 200ms, then drag it vertically to place it in the list. The new sequence is saved automatically!</div>
                    <DragAndDropVisual />
                </div>
            ),
            target: () => document.querySelector('tbody tr') || null,
        },
        {
            title: 'Mouse Actions',
            description: (
                <div>
                    <div>Use simple mouse actions to grab, move, and position project rows:</div>
                    <MouseVisual />
                </div>
            ),
            target: () => document.querySelector('tbody tr') || null,
        }
    ], []);

    const tableColumns = useMemo(() => {
        const base = [];
        
        base.push({
            title: '',
            key: 'star',
            width: '45px',
            align: 'center',
            render: (_, record) => (
                <span
                    style={{ cursor: 'pointer', display: 'inline-flex', padding: '4px' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStar(record.id, !record.isStarred);
                    }}
                >
                    {record.isStarred ? (
                        <StarFilled style={{ color: '#faad14', fontSize: '16px' }} />
                    ) : (
                        <StarOutlined style={{ color: '#bfbfbf', fontSize: '16px' }} />
                    )}
                </span>
            )
        });

        base.push(...columns);

        base.push({
            title: 'Actions',
            key: 'actions',
            width: '80px',
            align: 'center',
            render: (_, record) => {
                const menuItems = [
                    {
                        key: 'view',
                        label: 'View Details',
                        icon: <EyeOutlined />,
                        onClick: () => openProjectDetail(record.id, record.name),
                    },
                    {
                        key: 'star',
                        label: record.isStarred ? 'Unstar Project' : 'Star Project',
                        icon: record.isStarred ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />,
                        onClick: () => handleToggleStar(record.id, !record.isStarred),
                    },
                ];

                return (
                    <Dropdown
                        menu={{ items: menuItems }}
                        trigger={['click']}
                        placement="bottomRight"
                    >
                        <span 
                            style={{ cursor: 'pointer', padding: '4px 8px', fontSize: '18px', display: 'inline-flex' }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <MoreOutlined />
                        </span>
                    </Dropdown>
                );
            }
        });
        return base;
    }, [columns, statusTab, STATUS_TAB, openProjectDetail, handleToggleStar]);

    return (
        <ListBodyLayout
            title="Projects"
            description="View and manage projects"
            actions={
                canCreateProject && (
                    <Button
                        type="button"
                        $primary
                        $blackOnLight
                        icon={<PlusOutlined aria-hidden />}
                        onClick={() => setCreateModalOpen(true)}
                        $padding="10px 20px"
                        $size="14px"
                    >
                        Create Project
                    </Button>
                )
            }
            kpiCards={[
                {
                    label: 'Total Projects',
                    value: total ?? 0,
                    icon: <AimOutlined style={{ color: 'var(--ant-color-primary, #1890ff)' }} />,
                    trend: 'all time',
                    trendPositive: true,
                },
                {
                    label: 'Active',
                    value: activeCount ?? 0,
                    icon: <CheckCircleOutlined style={{ color: '#10b981' }} />,
                    trend: 'in progress',
                    trendPositive: true,
                },
                {
                    label: 'With Client',
                    value: withClientCount ?? 0,
                    icon: <UserOutlined style={{ color: '#8b5cf6' }} />,
                    trend: 'assigned',
                    trendPositive: true,
                },
                {
                    label: 'High Priority',
                    value: highPriorityCount ?? 0,
                    icon: <WarningOutlined style={{ color: '#ef4444' }} />,
                    trend: 'requires attention',
                    trendPositive: false,
                },
            ]}
        >
            <Container
                $display="flex"
                $justifyContent="space-between"
                $alignItems="center"
                $flexWrap="wrap"
                $gap="12px"
                $marginBottom="24px"
            >
                <SliderTab
                    items={[
                        { key: STATUS_TAB.ALL, label: 'All' },
                        { key: STATUS_TAB.STARRED, label: 'Starred' },
                        { key: STATUS_TAB.ACTIVE, label: 'Active' },
                        { key: STATUS_TAB.PENDING, label: 'Pending' },
                        { key: STATUS_TAB.INACTIVE, label: 'Inactive' },
                    ]}
                    activeKey={statusTab}
                    onChange={setStatusTab}
                    fitContent
                />
                <Container $display="flex" $alignItems="center" $gap="8px" style={{ flexShrink: 0 }}>
                    <TenantToolbarSearchWrap>
                        <span className="tenant-toolbar-search-icon" aria-hidden>
                            <SearchOutlined />
                        </span>
                        <TenantToolbarSearchInput
                            type="search"
                            placeholder="Search"
                            value={searchText}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                            aria-label="Search projects by name"
                        />
                    </TenantToolbarSearchWrap>
                    <ToolbarFilterButton
                        title="Filter projects"
                        open={filterModalOpen}
                        onOpenChange={setFilterModalOpen}
                        hasActiveFilters={hasActiveFilters}
                        onClearApplied={clearFilters}
                        onApply={applyFiltersFromModal}
                        onClearDraft={clearFilterDraft}
                    >
                        <Form layout="vertical" style={{ width: '100%', padding: '0 8px' }}>
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <Form.Item label="Priority" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="All priorities"
                                            value={filterPriorityDraft}
                                            onChange={(v) => setFilterPriorityDraft(v ?? undefined)}
                                            options={PRIORITY_OPTIONS}
                                            allowClear
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Project Type" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="All types"
                                            value={filterProjectTypeDraft}
                                            onChange={(v) => setFilterProjectTypeDraft(v ?? undefined)}
                                            options={PROJECT_TYPE_OPTIONS}
                                            allowClear
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Template" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="All templates"
                                            value={filterTemplateIdDraft}
                                            onChange={(v) => setFilterTemplateIdDraft(v ?? undefined)}
                                            options={templateOptions}
                                            allowClear
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Category" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="All categories"
                                            value={filterCategoryIdDraft}
                                            onChange={(v) => setFilterCategoryIdDraft(v ?? undefined)}
                                            options={categoryOptions}
                                            allowClear
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Client Type" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="All client types"
                                            value={filterClientTypeDraft}
                                            onChange={(v) => setFilterClientTypeDraft(v ?? undefined)}
                                            options={CLIENT_TYPE_OPTIONS}
                                            allowClear
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Client" style={{ marginBottom: 0 }}>
                                        <Select
                                            placeholder="All clients"
                                            value={filterClientIdDraft}
                                            onChange={(v) => setFilterClientIdDraft(v ?? undefined)}
                                            options={clientOptions}
                                            allowClear
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Form>
                    </ToolbarFilterButton>
                    <TenantToolbarIconBtn
                        type="button"
                        aria-label="Refresh projects"
                        aria-busy={projectsListRefetching}
                        onClick={() => {
                            if (!projectsListRefetching) refetchProjects();
                        }}
                    >
                        <TenantToolbarRefreshSpin $spin={projectsListRefetching}>
                            <RefreshCwIcon size={18} />
                        </TenantToolbarRefreshSpin>
                    </TenantToolbarIconBtn>
                    {statusTab === STATUS_TAB.ALL && (
                        <TenantToolbarIconBtn
                            type="button"
                            aria-label="Sorting Guide"
                            onClick={() => setTourOpen(true)}
                            ref={tourBtnRef}
                            style={{ marginLeft: '8px' }}
                        >
                            <InfoCircleOutlined style={{ fontSize: 18 }} />
                        </TenantToolbarIconBtn>
                    )}
                </Container>
            </Container>

            <Table
                columns={tableColumns}
                dataSource={tableData}
                pagination={statusTab !== STATUS_TAB.ALL}
                sortable={statusTab === STATUS_TAB.ALL}
                onReorder={(newData) => {
                    const ids = newData.map(item => item.id);
                    handleReorderProjects(ids);
                }}
                onRow={(record) => ({
                    onClick: (e) => {
                        const cellIndex = e.target.closest('td')?.cellIndex;
                        if (cellIndex === 0 || cellIndex === tableColumns.length - 1) {
                            return;
                        }
                        if (!e.currentTarget.contains(e.target)) {
                            return;
                        }
                        if (e.target.closest('.ant-dropdown-trigger')) {
                            return;
                        }
                        openProjectDetail(record.id, record.name);
                    },
                    style: { cursor: 'pointer' },
                })}
            />

            <CreateProjectModal
                open={createModalOpen}
                onCancel={closeCreateModal}
                initialValues={createInitialValues}
            />

            <Tour open={tourOpen} onClose={() => setTourOpen(false)} steps={tourSteps} />
        </ListBodyLayout>
    );
};

export default Projects;
